import { BulkApprovalBar } from "../BulkApprovalBar";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import Workspaces from "@mui/icons-material/Workspaces";
import CategoryOutlined from "@mui/icons-material/CategoryOutlined";
import SubjectOutlined from "@mui/icons-material/SubjectOutlined";
import CurrencyExchange from "@mui/icons-material/CurrencyExchange";
import DateRange from "@mui/icons-material/DateRange";
import TransferWithinAStation from "@mui/icons-material/TransferWithinAStation";
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import {
  checkEditorPermission,
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { showToast } from "services/utils/Status";
import { ExpenseDetailPanel } from "./DetailPanel";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { ExpenseStatusCards } from "./ExpenseStatusCards";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  bulkSelectionCheckboxSx,
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import { resolveRangeLabel } from "../dateRangeUtils";
import {
  fetchTeamClaims,
  fromApiClaim,
  reviewClaims,
  reviewOutcome,
  useActiveReimbursementTypes,
} from "pages/PayRoll/Shared/reimbursementApi";
import {
  ClaimFootnote,
  ClaimRequestPanel,
  ClaimStatusChip,
  PaidInCell,
  formatDay,
  plainAmount,
  toListRow,
} from "pages/PayRoll/Shared/reimbursementClaimUi";

// Team Workspace > Approvals > Reimbursement. Team members' reimbursement
// claims with the standard Approve list layout (status cards, sticky
// CustomFilter toolbar, saved views, bulk approve / reject) and a claim
// panel with limit checks and the payroll progress.
// Data: GET / PUT team_zone/approve/expense/teamworkspace-expense-approve/
// (list; approve / reject one or many) and POST expense/ for New Request,
// see pages/PayRoll/Shared/reimbursementApi.js.

// Employee from list/employee (same list the other Team Workspace screens
// use), for the New Request picker and the employee card details (ID,
// department, designation, photo) the claim API doesn't send.
const toEmployee = (member) => ({
  id: member.user_id ?? member.id,
  name: member.user_name || member.label || "",
  code: member.employee_id || member.employee_code || member.emp_id || "",
  department: member.department_name || member.department || "",
  designation: member.designation_name || member.designation || member.type || "",
  photo: member.image_url || "",
  raw: member,
});

// Same "Default View" sentinel pattern as the other split screens.
const EXPENSE_LIST_DEFAULT_VIEW_ID = "__default_reimbursement_list_view__";

const COLUMN_HEADERS = [
  { key: "request_id", name: "Request ID", type: "string", icon: <ConfirmationNumberOutlined fontSize="small" /> },
  { key: "employee_name", name: "Employee", type: "string", icon: <Workspaces fontSize="small" /> },
  { key: "type_name", name: "Reimbursement Type", type: "autocomplete", icon: <CategoryOutlined fontSize="small" /> },
  { key: "purpose", name: "Purpose / Description", type: "string", icon: <SubjectOutlined fontSize="small" /> },
  { key: "amount", name: "Amount (₹)", type: "number", icon: <CurrencyExchange fontSize="small" /> },
  { key: "bill_date", name: "Bill Date", type: "date", icon: <DateRange fontSize="small" /> },
  { key: "status_label", name: "Status", type: "autocomplete", icon: <TransferWithinAStation fontSize="small" /> },
  { key: "paid_in", name: "Paid In", type: "autocomplete", icon: <AccountBalanceOutlined fontSize="small" /> },
  { key: "submitted_on", name: "Submitted On", type: "date", icon: <EventAvailableOutlined fontSize="small" /> },
];

const renderCell = (row, key, selected) => {
  switch (key) {
    case "employee_name":
      return (
        <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>{row.employee_name?.charAt(0)?.toUpperCase()}</Mui.Avatar>
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ fontWeight: selected ? 700 : 500, fontSize: 14, color: designColors.slate900 }} noWrap>
              {row.employee_name || "N/A"}
            </Mui.Typography>
            <Mui.Typography sx={designType.captionText} noWrap>{row.employee_code}</Mui.Typography>
          </Mui.Box>
        </Mui.Stack>
      );
    case "request_id":
      return <Mui.Typography sx={{ fontWeight: 600, color: designColors.slate800, fontSize: 14 }}>{row.request_id}</Mui.Typography>;
    case "amount":
      return <Mui.Box sx={{ textAlign: "right", fontWeight: 600 }}>{plainAmount(row.amount)}</Mui.Box>;
    case "bill_date":
    case "submitted_on":
      return <TruncatedTableValue value={formatDay(row[key])} />;
    case "status_label":
      return <ClaimStatusChip status={row.status} />;
    case "paid_in":
      return <PaidInCell row={row} />;
    default:
      return <TruncatedTableValue value={row[key] ?? "N/A"} />;
  }
};

const ExpenseContent = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const { domain, userDetails, topbarCompanyName, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const { types } = useActiveReimbursementTypes(domain, topbarCompanyId);
  const { result: membersData } = useFetch(
    `${domain}list/employee?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );
  const employees = useMemo(
    () => (Array.isArray(membersData) ? membersData : [])
      .filter((member) => member?.user_name && member.user_name.toLowerCase() !== "all")
      .map(toEmployee)
      .filter((member) => member.id != null && String(member.id) !== String(userDetails?.id)),
    [membersData, userDetails?.id],
  );
  const [apiClaims, setApiClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  // Full-page loader only until the first list arrives; reloads after an
  // approve / reject keep the list on screen.
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    if (!domain) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    fetchTeamClaims({ domain, companyId: topbarCompanyId, signal: controller.signal })
      .then((rows) => { if (!controller.signal.aborted) setApiClaims(rows); })
      .catch((error) => { if (!controller.signal.aborted) setLoadError(error.message); })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setLoadedOnce(true);
      });
    return () => controller.abort();
  }, [domain, topbarCompanyId, reload]);

  const claims = useMemo(() => {
    const byId = new Map(employees.map((member) => [String(member.id), member]));
    return apiClaims.map((row) => {
      const claim = fromApiClaim(row, { self: false, types });
      const member = byId.get(String(claim.userId));
      return member
        ? { ...claim, employee: { ...claim.employee, name: member.name || claim.employee.name, code: claim.employee.code || member.code, department: claim.employee.department || member.department, designation: claim.employee.designation || member.designation, photo: claim.employee.photo || member.photo } }
        : claim;
    });
  }, [apiClaims, types, employees]);
  const refresh = useCallback(() => setReload((value) => value + 1), []);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const containerRef = useRef(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [expenseFilterData, setExpenseFilterData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  const [dateRangeLabel, setDateRangeLabel] = useState(dateRangeLabelProp || "Last 7 Days");

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);

  // The Approval Center asks child screens to reload through reCallApi.
  useEffect(() => {
    if (!reCallApi) return;
    refresh();
    setReCallApi(false);
  }, [reCallApi, setReCallApi, refresh]);

  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [bulkRejectReasonError, setBulkRejectReasonError] = useState("");
  const viewOpen = Boolean(selectedId);
  const { expanded: expensePanelExpanded, toggleExpanded: toggleExpensePanelExpanded } = useExpandablePanel(viewOpen);

  // Team members' claims inside the picked date range (by submitted date).
  const expenseData = useMemo(() => {
    const from = startDate ? moment(startDate).format("YYYY-MM-DD") : "";
    const to = endDate ? moment(endDate).format("YYYY-MM-DD") : "";
    return claims
      .map(toListRow)
      .filter((row) => !from || !to || (row.submitted_on >= from && row.submitted_on <= to));
  }, [claims, endDate, startDate]);

  // Until CustomFilter reports its first result, show every row (so the
  // table doesn't flash empty on first render).
  const filteredRows = expenseFilterData ?? expenseData;

  const check_access = checkEditorPermission(userScreenAccess?.module_details, "Approvals", "Expense");
  // Approvers' New Request claims are saved as Approved (old Apply rule).
  const hasApproverAccess =
    checkFirstLevelApproverPermission(userScreenAccess?.module_details, "Approvals", "Expense") ||
    checkSecondLevelApproverPermission(userScreenAccess?.module_details, "Approvals", "Expense");

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () => setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setExpenseFilterData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const expenseStatusCounts = useMemo(
    () =>
      expenseData.reduce(
        (acc, row) => {
          acc["Total Requests"] += 1;
          acc[row.status] = (acc[row.status] || 0) + 1;
          return acc;
        },
        { "Total Requests": 0, Pending: 0, Approved: 0, Paid: 0, Rejected: 0, Cancelled: 0 },
      ),
    [expenseData],
  );

  const visibleExpenseData = useMemo(
    () => (statusCardFilter ? filteredRows.filter((row) => row.status === statusCardFilter) : filteredRows),
    [filteredRows, statusCardFilter],
  );

  const clearSelection = () => {
    setSelectedRowKeys(new Set());
    setBulkRejectReason("");
    setBulkRejectReasonError("");
  };

  const handleStatusCardClick = (key) => {
    clearSelection();
    setShowBulkRejectDialog(false);
    setPage(0);
    setSelectedId(null);
    setStatusCardFilter(key === "Total Requests" ? "" : key);
  };

  const handleExpenseDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate?.(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
    setPage(0);
  };

  // Bulk approve / reject: only Pending rows can be selected, and only while
  // the Pending card is picked (same as the other Approve screens).
  const isSelected = (row) => selectedRowKeys.has(row.id);
  const pendingRows = useMemo(() => visibleExpenseData.filter((row) => row.status === "Pending"), [visibleExpenseData]);

  const toggleRow = (row) => {
    if (row.status !== "Pending") return;
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  const isAllSelected = pendingRows.length > 0 && pendingRows.every((row) => isSelected(row));
  const isIndeterminate = selectedRowKeys.size > 0 && selectedRowKeys.size < pendingRows.length;
  const toggleAll = () => setSelectedRowKeys(isAllSelected ? new Set() : new Set(pendingRows.map((row) => row.id)));
  const selectedRows = visibleExpenseData.filter((row) => isSelected(row));

  const hasDecisionAccess = () => {
    if (check_access) return true;
    showToast("error", "Access denied: You are not authorized to approve or reject these requests");
    return false;
  };

  const runReview = async (status, verb, reason) => {
    setReviewing(true);
    try {
      const result = await reviewClaims({
        domain,
        companyId: topbarCompanyId,
        ids: selectedRows.map((row) => row.id),
        status,
        reason,
      });
      const outcome = reviewOutcome(result, verb);
      showToast(outcome.type, outcome.text);
      clearSelection();
      setShowBulkRejectDialog(false);
      refresh();
    } catch (error) {
      showToast("error", error.message || `Couldn't update the claims. Please try again.`);
    } finally {
      setReviewing(false);
    }
  };

  const handleBulkApprove = () => {
    if (!selectedRows.length || reviewing || !hasDecisionAccess()) return;
    runReview("Approved", "approved");
  };

  const handleBulkReject = () => {
    if (!selectedRows.length || reviewing || !hasDecisionAccess()) return;
    const reason = bulkRejectReason.trim();
    if (!reason) return setBulkRejectReasonError("Reject reason is required");
    if (reason.length < 3) return setBulkRejectReasonError("Minimum 3 characters required");
    return runReview("Rejected", "rejected", reason);
  };

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const expenseColumnHeaders = COLUMN_HEADERS;

  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_reimbursement_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: EXPENSE_LIST_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || EXPENSE_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== EXPENSE_LIST_DEFAULT_VIEW_ID) return prev;
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = expenseColumnHeaders;

  const storedDefaultView = useMemo(
    () => savedViewsForScope.find((view) => view.id === EXPENSE_LIST_DEFAULT_VIEW_ID) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () => savedViewsForScope.filter((view) => view.id !== EXPENSE_LIST_DEFAULT_VIEW_ID),
    [savedViewsForScope],
  );

  const defaultView = useMemo(() => {
    if (storedDefaultView?.isCustomDefault) return storedDefaultView;
    return {
      id: EXPENSE_LIST_DEFAULT_VIEW_ID,
      name: "Default View",
      headerConfig: baseDefaultHeaders.map((header) => ({ key: header.key, name: header.name })),
      headerKeys: baseDefaultHeaders.map((header) => header.key),
      isDefaultView: true,
    };
  }, [baseDefaultHeaders, storedDefaultView]);

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === EXPENSE_LIST_DEFAULT_VIEW_ID) return defaultView;
    return customSavedViews.find((view) => view.id === effectiveViewId) || defaultView;
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(expenseColumnHeaders.map((header) => [header.key, header]));

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return { ...baseHeader, name: entry.name?.trim() || baseHeader.name };
        })
        .filter(Boolean);
      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = expenseColumnHeaders.filter((header) => selectedKeys.has(header.key));
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, expenseColumnHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(EXPENSE_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === EXPENSE_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(EXPENSE_LIST_DEFAULT_VIEW_ID);
        return;
      }
      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) setCurrentViewId(EXPENSE_LIST_DEFAULT_VIEW_ID);
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || EXPENSE_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(EXPENSE_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={expenseColumnHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={check_access}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === EXPENSE_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      check_access,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      expenseColumnHeaders,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  const showCheckboxes = statusCardFilter === "Pending";
  const columnCount = visibleHeaders.length + (showCheckboxes ? 1 : 0);
  const headTop = APP_BAR_OFFSET + customFilterHeight;

  if (loading && !loadedOnce) return <Loader />;

  return (
    <React.Fragment>
      <Box sx={{ flexGrow: 1, width: "100%", overflow: "visible" }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>Reimbursement Claims</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review your team's reimbursement claims. Approved claims are paid with their salary.
            </Mui.Typography>
          </Box>
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }}>
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleExpenseDateRange}
              includeCurrentDate
              showToday
              allowFutureDate
              sx={{ minHeight: 46 }}
            />
            <NewRequestButton
              hasAccess={check_access}
              deniedMessage="Access denied: You are not authorized to create a reimbursement request"
              onClick={() => setIsCreatePanelOpen(true)}
            />
          </Mui.Stack>
        </Box>

        <ExpenseStatusCards counts={expenseStatusCounts} activeFilter={statusCardFilter} onCardClick={handleStatusCardClick} />

        {loadError ? (
          <Mui.Alert
            severity="error"
            sx={{ mb: 1.5 }}
            action={<Mui.Button color="inherit" onClick={refresh}>Retry</Mui.Button>}
          >
            {loadError}
          </Mui.Alert>
        ) : null}

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title="Reimbursement Requests"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={expenseData}
            filteredData={visibleExpenseData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            labeledToolbar={true}
            searchPlaceholder="Search by claim ID, employee, type or purpose"
          />
        </Box>
        <BulkApprovalBar
          selectedCount={showCheckboxes ? selectedRowKeys.size : 0}
          loading={reviewing}
          onReject={() => setShowBulkRejectDialog(true)}
          onApprove={handleBulkApprove}
        />
        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
            <ApproveTable stickyHeader sx={{ minWidth: 1100, width: "100%", tableLayout: "fixed" }}>
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {showCheckboxes && (
                    <Mui.TableCell padding="checkbox" sx={{ ...approveTableHeadCell, top: headTop }}>
                      {pendingRows.length > 0 && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={toggleAll}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                  {visibleHeaders.map((head) => (
                    <Mui.TableCell
                      key={head.key}
                      sx={{
                        ...approveTableHeadCell,
                        top: headTop,
                        ...(head.key === "amount" ? { textAlign: "right" } : {}),
                        ...(head.key === "purpose" ? { width: "18%" } : {}),
                        ...(head.key === "employee_name" ? { width: "14%" } : {}),
                      }}
                    >
                      <TruncatedTableValue value={head.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleExpenseData.length === 0 ? (
                  <TableEmptyState dataLength={expenseData} colSpan={columnCount} />
                ) : (
                  visibleExpenseData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const selected = viewOpen && row.id === selectedId;
                    return (
                      <Mui.TableRow
                        key={row.id}
                        onClick={() => setSelectedId(row.id)}
                        sx={{
                          cursor: "pointer",
                          ...designShell.tableRowHover,
                          ...(selected ? designShell.neutralSelectedRow : {}),
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        {showCheckboxes && (
                          <Mui.TableCell padding="checkbox" sx={approveTableBodyCell} onClick={(event) => event.stopPropagation()}>
                            {row.status === "Pending" && (
                              <Mui.Checkbox size="small" sx={bulkSelectionCheckboxSx} checked={isSelected(row)} onChange={() => toggleRow(row)} />
                            )}
                          </Mui.TableCell>
                        )}
                        {visibleHeaders.map((head) => (
                          <Mui.TableCell key={head.key} data-label={head.name} sx={approveTableBodyCell}>
                            {renderCell(row, head.key, selected)}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    );
                  })
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
          {visibleExpenseData.some((row) => row.status === "Pending") ? <ClaimFootnote /> : null}
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleExpenseData.length,
              itemLabel: "reimbursement requests",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>
      <ClaimRequestPanel
        open={isCreatePanelOpen}
        onClose={() => setIsCreatePanelOpen(false)}
        mode="team"
        domain={domain}
        companyId={topbarCompanyId}
        employees={employees}
        claims={claims}
        approve={hasApproverAccess}
        onSubmitted={refresh}
      />
      <ExpenseDetailPanel
        open={viewOpen}
        row={selectedId ? claims.find((row) => row.id === selectedId) || null : null}
        claims={claims}
        types={types}
        domain={domain}
        companyId={topbarCompanyId}
        onChanged={refresh}
        onClose={() => setSelectedId(null)}
        expanded={expensePanelExpanded}
        onToggleExpand={toggleExpensePanelExpanded}
      />
      <GlobalConfirmDialog
        open={showBulkRejectDialog}
        title="Reject Selected Requests"
        onCancel={() => {
          if (reviewing) return;
          setShowBulkRejectDialog(false);
          setBulkRejectReason("");
          setBulkRejectReasonError("");
        }}
        onConfirm={handleBulkReject}
        confirmLabel="Reject"
        cancelLabel="Cancel"
        danger
        maxWidth="sm"
        reasonValue={bulkRejectReason}
        reasonError={bulkRejectReasonError}
        onReasonChange={(value) => {
          setBulkRejectReason(value);
          if (bulkRejectReasonError) setBulkRejectReasonError("");
        }}
        reasonPlaceholder="Enter a reason for rejection"
        reasonMinRows={2}
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Reason - applied to all {selectedRowKeys.size} selected request(s).
        </Mui.Typography>
      </GlobalConfirmDialog>
    </React.Fragment>
  );
};

export const Expense = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel,
  handleStartDate,
  subModuleId,
}) => (
  <ExpenseContent
    setReCallApi={setReCallApi}
    reCallApi={reCallApi}
    startDate={startDate}
    endDate={endDate}
    dateRangeLabel={dateRangeLabel}
    handleStartDate={handleStartDate}
    subModuleId={subModuleId}
  />
);
